import { Button } from '@gerdau/hefesto/Button';
import { Title } from '@gerdau/hefesto/Title';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FabricationContractsSelect from '../../components/FabricationContractsSelect';
import IsManagerFabricationContractInput from '../../components/IsManagerFabricationContractInput';
import { ERoutes } from '../../routes';
import { ApplicationState } from '../../store';
import {
  loadCustomerContractsV2,
  updateConstructionRegistrationSettings
} from '../../store/ducks/contracts-and-constructions/actions';
import { INITIAL_CONSTRUCTION_REGISTRATION } from '../../store/ducks/contracts-and-constructions/types';
import { loadContractsV2 } from '../../store/ducks/fabrication-contracts/actions';
import {
  EFabricationContractsStatus,
  IFabricationContract
} from '../../store/ducks/fabrication-contracts/types';
import { loadMockups } from '../../store/ducks/fabrication-mockups/actions';
import { loadProjects } from '../../store/ducks/fabrication-projects/actions';
import { sendTagging } from '../../store/ducks/tagging/actions';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { getTaggingInformationScreen } from '../../utils/Tagging';
import { textEllipsis } from '../../utils/formatStrings';
import { EConstructionsRoutes } from '../ContractsAndConstructions/contractsAndConstructions.routes';
import ProjectsMockupTabs, { EProjectsMockupsTab } from './ProjectsMockupTabs';
import {
  AntdButton,
  CheckNewContractModal,
  EmptyContainer,
  ProjectsMockupFilterContainer,
  ProjectsMockupHeader
} from './styles';

interface ProjectsMockupProps {
  selectedTab: EProjectsMockupsTab;
  setSelectedTab: (tab: EProjectsMockupsTab) => void;
}

const ProjectsMockup: React.FC<ProjectsMockupProps> = ({
  selectedTab,
  setSelectedTab
}) => {
  const dispatch = useDispatch();
  const userState = useSelector((state: ApplicationState) => state.user);
  const projectState = useSelector(
    (state: ApplicationState) => state.fabricationProjects
  );
  const mockupState = useSelector(
    (state: ApplicationState) => state.fabricationMockups
  );
  const contractsState = useSelector(
    (state: ApplicationState) => state.fabricationContracts
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:MaquetesEProjetos',
        page: window.location.pathname,
        event: 'page_view'
      })
    );
  }, [dispatch]);

  const history = useHistory();

  const contractAndConstructionsContractsManager = useSelector(
    (state: ApplicationState) =>
      state.contractsAndConstructions.contractsManager
  );

  const [selectedContractNumber, setSelectedContractNumber] = useState(
    mockupState.contractNumber
  );

  const [canShowNewContractWarningModal, setCanShowNewContractWarningModal] =
    useState(false);

  const getSelectedContract = (contractNumber: string) => {
    return contractsState.contracts?.find(
      (c) => c.contractNumber === contractNumber
    );
  };

  const onSelectContract = (contractNumber: string) => {
    setSelectedContractNumber(contractNumber);

    const selectedContract = getSelectedContract(contractNumber);
    if (
      !!selectedContract &&
      selectedContract.status === EFabricationContractsStatus.NEW_CONTRACT
    ) {
      setCanShowNewContractWarningModal(true);
    }
  };

  const handleLoadProjects = () => {
    if (!selectedContractNumber) {
      return;
    }
    const needToLoadContract =
      userState.isManager &&
      !contractsState.contracts?.find(
        (c) => c.contractNumber === selectedContractNumber
      );

    dispatch(loadProjects(selectedContractNumber, needToLoadContract));
  };

  const handleLoadMockups = () => {
    if (!selectedContractNumber) {
      return;
    }
    dispatch(loadMockups(selectedContractNumber));
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    'maquetes-e-projetos-buscar'
  );

  const onSearchClick = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:buscar',
        itemDescription,
        event: 'click',
        contratoObra: selectedContractNumber
      })
    );

    if (selectedTab === EProjectsMockupsTab.MOCKUPS) {
      handleLoadMockups();
    } else {
      handleLoadProjects();
    }
  };

  const onChangeSelectedTab = (tab: EProjectsMockupsTab) => {
    if (
      tab === EProjectsMockupsTab.PROJECTS &&
      (selectedContractNumber !== projectState.contractNumber ||
        projectState.needToRefreshProjects) &&
      !projectState.loading &&
      !projectState.error
    ) {
      handleLoadProjects();
    } else if (
      tab === EProjectsMockupsTab.MOCKUPS &&
      selectedContractNumber !== mockupState.contractNumber &&
      !mockupState.loading &&
      !mockupState.error
    ) {
      handleLoadMockups();
    }
    setSelectedTab(tab);
  };

  const getCustomerOrderText = (): string | undefined => {
    if (
      !selectedContractNumber ||
      selectedContractNumber.length < 6 ||
      projectState.loading ||
      !contractsState.contracts ||
      contractsState.contracts.length === 0
    ) {
      return undefined;
    } else {
      const contract = contractsState.contracts?.find(
        (cont) => cont.contractNumber === selectedContractNumber
      );
      return (
        contract &&
        (`${contract.contractNumber} - ${textEllipsis(
          contract.customerOrder,
          24
        )}` ||
          undefined)
      );
    }
  };

  useEffect(() => {
    if (
      !userState.isManager &&
      contractsState.contracts === undefined &&
      !contractsState.loading &&
      !contractsState.error
    ) {
      dispatch(loadContractsV2());
    }
  });

  useEffect(() => {
    handleLoadMockups();
    handleLoadProjects();
  }, []);

  const getContracts = (): IFabricationContract[] => {
    if (contractsState.contracts) {
      return contractsState.contracts.filter(
        (contract) =>
          userState.roles.ordersInputCustomers.includes(contract.shipTo.code) ||
          userState.roles.ordersInputCustomers.includes(contract.soldTo.code)
      );
    } else {
      return [];
    }
  };

  const goToConstructionRegistration = () => {
    if (!selectedContractNumber) {
      return;
    }

    const selectedContract = getSelectedContract(selectedContractNumber);
    if (selectedContract) {
      if (
        selectedContract.soldTo.code !==
          contractAndConstructionsContractsManager.selectedCustomerId ||
        contractAndConstructionsContractsManager.error
      ) {
        const customerType = userState.searchCustomerType;
        const customerId =
          customerType === ECustomerTypeView.EMITTER
            ? selectedContract.soldTo.code
            : selectedContract.shipTo.code;

        dispatch(loadCustomerContractsV2(customerId));
      }
      dispatch(
        updateConstructionRegistrationSettings({
          ...INITIAL_CONSTRUCTION_REGISTRATION,
          contractNumber: selectedContract.contractNumber
        })
      );
      history.push(
        `${ERoutes.CONTRACTS_AND_CONSTRUCTIONS}${EConstructionsRoutes.CONSTRUCTION_REGISTRATION}`
      );
    }
  };

  return (
    <>
      <ProjectsMockupHeader>
        <Title level={1}>Maquetes e Projetos</Title>
        <span className="subtitle">
          Para associar os pedidos aos ambientes das obras, você pode criar
          maquetes. Não será possível editar uma maquete após associá-la a um
          <br /> determinado pedido da obra.
        </span>
        <ProjectsMockupFilterContainer>
          {userState.isManager && (
            <IsManagerFabricationContractInput
              title="Contrato"
              loading={contractsState.loading || mockupState.loading}
              contractError={false}
              contractNumber={selectedContractNumber || ''}
              setContractNumber={setSelectedContractNumber}
              customerOrder={getCustomerOrderText()}
            />
          )}

          {!userState.isManager && (
            <>
              <FabricationContractsSelect
                contractNumber={selectedContractNumber}
                contracts={getContracts()}
                onChange={onSelectContract}
                showClosedContractMessage
                loading={
                  contractsState.loading ||
                  projectState.loading ||
                  mockupState.loading
                }
              />
            </>
          )}
          <Button
            name="filterProjectsMockupBtn"
            onClick={onSearchClick}
            loading={
              contractsState.loading ||
              projectState.loading ||
              mockupState.loading
            }
            disabled={
              contractsState.loading ||
              projectState.loading ||
              mockupState.loading ||
              !selectedContractNumber ||
              selectedContractNumber.length < 4
            }
          >
            Buscar
          </Button>
        </ProjectsMockupFilterContainer>
      </ProjectsMockupHeader>
      {mockupState.contractNumber &&
        !mockupState.error &&
        !projectState.error && (
          <ProjectsMockupTabs
            selectedTab={selectedTab}
            setSelectedTab={onChangeSelectedTab}
          />
        )}
      {!mockupState.contractNumber &&
        !mockupState.error &&
        !projectState.error && (
          <EmptyContainer>
            <span>
              Para ver suas maquetes e projetos,
              <strong> selecione um contrato de obra</strong>
            </span>
          </EmptyContainer>
        )}

      <CheckNewContractModal
        visible={canShowNewContractWarningModal}
        width={600}
        closable={false}
        footer={[
          <AntdButton
            key="cancel"
            onClick={() => {
              setCanShowNewContractWarningModal(false);
              setSelectedContractNumber(mockupState.contractNumber);
            }}
          >
            Cancelar
          </AntdButton>,
          <AntdButton
            type="primary"
            key="confirm"
            onClick={goToConstructionRegistration}
          >
            Sim
          </AntdButton>
        ]}
      >
        <h3>
          Para cadastrar maquetes neste contrato, finalize o cadastro da obra.
        </h3>

        <span>Deseja finalizar o cadastro agora?</span>
      </CheckNewContractModal>
    </>
  );
};

export default ProjectsMockup;
